import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../context/LanguageContext';
import './LinksPanel.css';

// videoId = embeddable YouTube video ID; omit for channel/stream pages (opens externally)

// Live Darshan source.
//
// This used to be a OneStream embed
// (player.onestream.live/embed?token=MzkzNTgzOQ==&type=up). OneStream
// retired that token scheme: the endpoint now rejects *every* token with
// HTTP 400 {"error":"Invalid token format"}, so the iframe rendered a raw
// JSON error blob to visitors instead of a player.
//
// We now embed the temple's own YouTube broadcast. Two things to know
// before changing this:
//   1. YouTube's channel-level auto-follow form
//      (`/embed/live_stream?channel=<id>`) does NOT work — it fails with
//      "video unavailable" / error 153 even while the channel is live.
//      Only a direct video id embeds correctly.
//   2. Because of (1), `videoId` points at a specific broadcast and must
//      be refreshed when the temple starts a new one. Grab the current id
//      from https://www.youtube.com/@mangalgrahamandir/live (the canonical
//      URL of that page is the live video).
// If the id goes stale or the stream is offline, the panel degrades to a
// branded "not broadcasting" card rather than failing silently.
const LIVE = {
  id: 'live_aarti',
  isLive: true,
  title:    { mr: 'थेट आरती', en: 'Live Aarti', hi: 'लाइव आरती' },
  subtitle: { mr: 'मंगलदेव ग्रह मंदिर – थेट प्रक्षेपण', en: 'Mangal Dev Grah Mandir – Live Stream', hi: 'मंगलदेव ग्रह मंदिर – लाइव' },
  channelId: 'UCw1nvZhXZNy_i0ayHNrZQNQ',
  videoId: 'nk5ywrr-3UI',
  url: 'https://www.youtube.com/@mangalgrahamandir/live',
};

// enablejsapi=1 lets us listen for the player's onError so a dead or
// non-embeddable video can fall back to the offline card.
const liveEmbedUrl = (videoId) =>
  `https://www.youtube.com/embed/${videoId}`
  + '?autoplay=1&mute=1&rel=0&playsinline=1&enablejsapi=1';

const VIDEOS = [
  {
    id: 'yt_murti_story',
    videoId: 'fZJT-IUoFM4',
    title: {
      mr: 'मंगल ग्रह मंदिर रहस्य | १९३३ ते १९९९ ची चमत्कारी कथा',
      en: 'Mangal Grah Mandir Mystery | 1933 Se 1999 Tak Ki Chamatkari Kahani | Mangal Dev',
      hi: 'मंगल ग्रह मंदिर रहस्य | 1933 से 1999 तक की चमत्कारी कहानी | मंगल देव',
    },
    subtitle: { mr: 'श्री मंगल ग्रह मंदिर, अमळनेर', en: 'Shree Mangal Graha Mandir, Amalner', hi: 'श्री मंगल ग्रह मंदिर, अमळनेर' },
  },
  {
    id: 'yt_mangal_birth',
    videoId: 'IoDMJq8wu38',
    title: {
      mr: 'मंगळ देवाची जन्मकथा | अंधक वध आणि महादेवांचा दिव्य क्रोध',
      en: 'Mangal Dev Birth Story | Andhak Vadh & Mahadev’s Divine Wrath',
      hi: 'मंगल देव की जन्म कथा | अंधक वध और महादेव का दिव्य क्रोध | Mangal Dev Birth Story Hindi',
    },
    subtitle: { mr: 'श्री मंगल ग्रह मंदिर, अमळनेर', en: 'Shree Mangal Graha Mandir, Amalner', hi: 'श्री मंगल ग्रह मंदिर, अमळनेर' },
  },
  {
    id: 'yt_aarti',
    videoId: 'KJCClqiCmRI',
    title: {
      mr: 'श्री मंगळदेव ग्रह मंदिर लाईव्ह आरती',
      en: 'Shri Mangaldev Graha Amalner — Live Aarti',
      hi: 'श्री मंगलदेव ग्रह मंदिर लाइव आरती',
    },
    subtitle: { mr: 'श्री मंगल ग्रह मंदिर, अमळनेर', en: 'Shree Mangal Graha Mandir, Amalner', hi: 'श्री मंगल ग्रह मंदिर, अमळनेर' },
  },
  {
    id: 'yt_video_1',
    videoId: 'r3Y2YVAl9z8',
    title: {
      mr: 'श्री मंगल ग्रह मंदिर, अमळनेरचे वैशिष्ट्य — श्री दिगंबर महाले सरांनी सांगितलेले',
      en: 'The Uniqueness of Shri Mangal Graha Mandir, Amalner — Narrated by Shri Digambhar Mahale Sir',
      hi: 'श्री मंगल ग्रह मंदिर, अमळनेर की विशेषता — श्री दिगंबर महाले सर द्वारा',
    },
    subtitle: { mr: 'श्री मंगल ग्रह मंदिर, अमळनेर', en: 'Shree Mangal Graha Mandir, Amalner', hi: 'श्री मंगल ग्रह मंदिर, अमळनेर' },
  },
  {
    id: 'yt_video_2',
    videoId: 'RFTi6tiWocI',
    title: {
      mr: 'जय मंगल देव | मंगल ग्रह मंदिर भजन',
      en: 'Jai Mangal Dev | Mangla Grah Mandir Bhajan | Powerful Mars Devotional Song',
      hi: 'जय मंगल देव | मंगल ग्रह मंदिर भजन',
    },
    subtitle: { mr: 'श्री मंगल ग्रह मंदिर, अमळनेर', en: 'Shree Mangal Graha Mandir, Amalner', hi: 'श्री मंगल ग्रह मंदिर, अमळनेर' },
  },
  {
    id: 'yt_video_3',
    videoId: 'noF2ZNcOEtE',
    title: {
      mr: 'श्री मंगळग्रह मंदिरात भूमी महायाग',
      en: 'Bhoomi Mahayag at Shri Mangal Graha Mandir',
      hi: 'श्री मंगल ग्रह मंदिर में भूमि महायाग',
    },
    subtitle: { mr: 'श्री मंगल ग्रह मंदिर, अमळनेर', en: 'Shree Mangal Graha Mandir, Amalner', hi: 'श्री मंगल ग्रह मंदिर, अमळनेर' },
  },
];

const WEBSITES = [
  {
    id: 'temple_site',
    icon: '🛕',
    title:    { mr: 'मंदिर वेबसाइट', en: 'Temple Website', hi: 'मंदिर वेबसाइट' },
    subtitle: { mr: 'www.mangalagrahmandir.in', en: 'www.mangalagrahmandir.in', hi: 'www.mangalagrahmandir.in' },
    url: 'https://www.mangalagrahmandir.in/',
  },
  {
    id: 'maha_tourism',
    icon: '🌄',
    title:    { mr: 'महाराष्ट्र पर्यटन', en: 'Maharashtra Tourism', hi: 'महाराष्ट्र पर्यटन' },
    subtitle: { mr: 'maharashtratourism.gov.in', en: 'maharashtratourism.gov.in', hi: 'maharashtratourism.gov.in' },
    url: 'https://maharashtratourism.gov.in/',
  },
];

const T = {
  heading:  { mr: 'मंदिर व्हिडिओ', en: 'Mandir Videos', hi: 'मंदिर वीडियो' },
  live:     { mr: '🔴 थेट प्रक्षेपण', en: '🔴 Live Streaming', hi: '🔴 लाइव स्ट्रीमिंग' },
  videos:   { mr: '📺 व्हिडिओ', en: '📺 Videos', hi: '📺 वीडियो' },
  websites: { mr: '🌐 उपयुक्त दुवे', en: '🌐 Useful Links', hi: '🌐 उपयोगी लिंक्स' },
  watchLive:{ mr: 'थेट पाहा', en: 'Watch Live', hi: 'लाइव देखें' },
  back:     { mr: '← मागे', en: '← Back', hi: '← वापस' },
  watchYT:  { mr: 'YouTube वर पाहा ↗', en: 'Watch on YouTube ↗', hi: 'YouTube पर देखें ↗' },
  offlineTitle: {
    mr: 'सध्या थेट प्रक्षेपण सुरू नाही',
    en: 'Live darshan is not streaming right now',
    hi: 'अभी लाइव प्रसारण नहीं हो रहा है',
  },
  offlineBody: {
    mr: 'आरतीच्या वेळी थेट दर्शन उपलब्ध होते. तोपर्यंत मंदिराचे मागील व्हिडिओ पाहू शकता.',
    en: 'Live darshan is broadcast at aarti times. Until then you can watch the temple’s past videos.',
    hi: 'आरती के समय लाइव दर्शन उपलब्ध होता है. तब तक आप मंदिर के पिछले वीडियो देख सकते हैं.',
  },
  offlineCta:  { mr: 'YouTube चॅनल उघडा ↗', en: 'Open YouTube channel ↗', hi: 'YouTube चैनल खोलें ↗' },
  offlineBack: { mr: 'मंदिर व्हिडिओ पाहा', en: 'Browse temple videos', hi: 'मंदिर वीडियो देखें' },
};

function ytThumb(videoId) {
  return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
}

const LinksPanel = ({ onClose, autoOpenLive = false }) => {
  const { language } = useLanguage();
  const panelRef = useRef(null);
  // activeEmbed: { type: 'video', videoId, title } | { type: 'iframe', url, title } | null
  // Initialise directly to the live embed when autoOpenLive is true so
  // the grid never flashes before the live stream renders.
  const [activeEmbed, setActiveEmbed] = useState(() => (
    autoOpenLive
      ? {
          type: 'iframe',
          url: liveEmbedUrl(LIVE.videoId),
          title: LIVE.title.en,
          allowAutoplay: true,
          fullscreen: true,
          isLiveStream: true,
        }
      : null
  ));
  // True once the YouTube player reports the broadcast can't be shown
  // (offline, removed, or embedding disabled). Drives the fallback card.
  const [liveUnavailable, setLiveUnavailable] = useState(false);
  const liveIframeRef = useRef(null);

  const t = (obj) => obj[language] || obj.en;

  // If the parent flips autoOpenLive on after mount, jump straight into
  // the live-stream embed instead of showing the videos grid.
  useEffect(() => {
    if (autoOpenLive) {
      setActiveEmbed((prev) => prev || ({
        type: 'iframe',
        url: liveEmbedUrl(LIVE.videoId),
        title: t(LIVE.title),
        allowAutoplay: true,
        fullscreen: true,
        isLiveStream: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenLive]);

  // Live embed lifecycle: hide the app footer while the stream is open,
  // and watch the YouTube player for an error so we can swap in the
  // offline card instead of leaving the visitor on a dead black frame.
  //
  // The old OneStream player had a fixed 1250×650 viewport, which forced a
  // manual cover-scale + touch-pan implementation here. The YouTube embed
  // is responsive, so all of that is gone.
  useEffect(() => {
    if (!activeEmbed?.fullscreen) return undefined;

    document.body.classList.add('lp-live-active');

    if (!activeEmbed.isLiveStream) {
      return () => document.body.classList.remove('lp-live-active');
    }

    setLiveUnavailable(false);

    let sawPlayer = false;
    let handshake = null;

    // With enablejsapi=1 the player stays silent until a listener
    // registers, so keep poking it until it answers.
    const ping = () => {
      try {
        liveIframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({ event: 'listening', id: 'lp-live', channel: 'widget' }),
          'https://www.youtube.com'
        );
      } catch { /* noop */ }
    };

    const onMessage = (e) => {
      if (e.origin !== 'https://www.youtube.com') return;
      // Only trust messages from the player we actually mounted.
      if (e.source !== liveIframeRef.current?.contentWindow) return;
      let data = e.data;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { return; }
      }
      if (!data || typeof data !== 'object') return;
      if (data.event === 'onReady' || data.event === 'infoDelivery') {
        sawPlayer = true;
        if (handshake) { clearInterval(handshake); handshake = null; }
      }
      // 2/5/100/101/150 all mean "this video can't play here".
      if (data.event === 'onError') setLiveUnavailable(true);
    };

    window.addEventListener('message', onMessage);
    handshake = setInterval(ping, 500);
    ping();

    // If the player never reports in at all, treat the embed as broken.
    const giveUp = setTimeout(() => { if (!sawPlayer) setLiveUnavailable(true); }, 12000);

    return () => {
      document.body.classList.remove('lp-live-active');
      window.removeEventListener('message', onMessage);
      if (handshake) clearInterval(handshake);
      clearTimeout(giveUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEmbed]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (activeEmbed) clearEmbed();
        else onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, activeEmbed]);

  // Auto-close on any pointerdown outside the panel — covers footer
  // buttons, header chips, and other floating controls that sit above
  // the overlay z-index. Mirrors the InfoPanel behaviour.
  useEffect(() => {
    const handlePointerDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [onClose]);

  // Clear embed to go back to grid
  const clearEmbed = () => {
    setActiveEmbed(null);
  };

  const handleVideoTileClick = (video) => {
    // Embeddable videos (those with a videoId) play inline inside the
    // panel via the YouTube embed iframe — no new tab / popup needed.
    // Desktop-only: mobile behavior is frozen and continues to open
    // YouTube externally (popup in kiosk, new tab otherwise).
    const isDesktop = typeof window !== 'undefined'
      && window.matchMedia('(min-width: 701px)').matches;
    if (video.videoId && isDesktop) {
      setActiveEmbed({
        type: 'video',
        videoId: video.videoId,
        title: t(video.title),
      });
      return;
    }
    const url = video.videoId
      ? `https://www.youtube.com/watch?v=${video.videoId}`
      : video.url;
    if (!url) return;
    // Embedding is blocked at the channel level (YouTube error 153),
    // so we open the watch page externally. In kiosk mode we MUST NOT
    // open a normal new tab — that would break out of the locked
    // fullscreen browser. Instead open a sized popup so the kiosk
    // session stays intact and the visitor can close it back to the
    // app. Outside kiosk mode a regular new tab is fine.
    let kiosk = false;
    try { kiosk = localStorage.getItem('kioskMode') === '1'; } catch { /* noop */ }
    if (kiosk) {
      const w = Math.min(960, window.screen.availWidth - 80);
      const h = Math.round(w * 9 / 16) + 60;
      const left = Math.round((window.screen.availWidth - w) / 2);
      const top  = Math.round((window.screen.availHeight - h) / 2);
      window.open(
        url,
        'yt_player',
        `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=no,noopener,noreferrer`
      );
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Broadcast live-stream activity so the app-level idle-refresh watcher
  // can suppress its inactivity timer while the live stream is playing.
  useEffect(() => {
    const isLive = !!(activeEmbed
      && activeEmbed.type === 'iframe'
      && activeEmbed.isLiveStream);
    try {
      window.dispatchEvent(new CustomEvent('liveStreamActive', { detail: isLive }));
    } catch { /* noop */ }
    return () => {
      // On unmount or before next effect, signal "not live" so the
      // idle watcher resumes.
      if (isLive) {
        try { window.dispatchEvent(new CustomEvent('liveStreamActive', { detail: false })); }
        catch { /* noop */ }
      }
    };
  }, [activeEmbed]);

  const handleSiteClick = (site) => {
    setActiveEmbed({ type: 'iframe', url: site.url, title: t(site.title) });
  };

  const content = (
    <div
      className={`lp-overlay${activeEmbed ? ' lp-overlay--player' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) { clearEmbed(); onClose(); } }}
      role="dialog"
      aria-modal="true"
      aria-label={t(T.heading)}
    >
      <div ref={panelRef} className={`lp-modal${activeEmbed ? ' lp-modal--player' : ''}${activeEmbed?.fullscreen ? ' lp-modal--live' : ''}`}>

        {/* ── Header ── */}
        {!activeEmbed?.fullscreen && (
          <div className="lp-header">
            {activeEmbed ? (
              <>
                <button className="lp-back-btn" onClick={clearEmbed} aria-label="Back">
                  {t(T.back)}
                </button>
                <h2 className="lp-heading lp-heading--player">{activeEmbed.title}</h2>
              </>
            ) : (
              <h2 className="lp-heading">{t(T.heading)}</h2>
            )}
            <button className="lp-close-btn" onClick={onClose} aria-label="Close">
              <svg className="lp-close-btn__icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" />
              </svg>
            </button>
          </div>
        )}
        {activeEmbed?.fullscreen && (
          <>
            {!liveUnavailable && (
              <span className="lp-live-badge" aria-hidden="true">
                <span className="lp-live-badge__dot" />
                <span className="lp-live-badge__text">Live</span>
              </span>
            )}
            <button className="lp-close-btn lp-close-btn--live" onClick={onClose} aria-label="Close">
              <svg className="lp-close-btn__icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" />
              </svg>
              <span className="lp-close-btn__text">Close</span>
            </button>
          </>
        )}

        {/* ── Inline embed player (YouTube video or generic iframe) ── */}
        {activeEmbed && (
          <div className={`lp-player-wrap${activeEmbed.fullscreen ? ' lp-player-wrap--live' : ''}`}>
            {activeEmbed.type === 'video' ? (
              <iframe
                key={activeEmbed.videoId}
                className="lp-player-iframe"
                src={`https://www.youtube.com/embed/${activeEmbed.videoId}?autoplay=1&mute=1&rel=0&playsinline=1`}
                title={activeEmbed.title}
                // This site is served with `Referrer-Policy: same-origin`, which
                // strips the referrer from cross-origin iframes. YouTube then
                // can't verify the embedding domain and fails with error 153,
                // so send the origin explicitly. (InfoPanel's map embed and the
                // Matterport showcase iframe already do the same.)
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            ) : activeEmbed.isLiveStream && liveUnavailable ? (
              <div className="lp-live-offline">
                <div className="lp-live-offline__icon" aria-hidden="true">📺</div>
                <div className="lp-live-offline__title">{t(T.offlineTitle)}</div>
                <div className="lp-live-offline__body">{t(T.offlineBody)}</div>
                <div className="lp-live-offline__actions">
                  <button
                    type="button"
                    className="lp-live-offline__btn lp-live-offline__btn--primary"
                    onClick={() => window.open(LIVE.url, '_blank', 'noopener,noreferrer')}
                  >
                    {t(T.offlineCta)}
                  </button>
                  <button
                    type="button"
                    className="lp-live-offline__btn"
                    onClick={clearEmbed}
                  >
                    {t(T.offlineBack)}
                  </button>
                </div>
              </div>
            ) : (
              <iframe
                key={activeEmbed.url}
                ref={activeEmbed.isLiveStream ? liveIframeRef : undefined}
                className="lp-player-iframe"
                src={activeEmbed.url}
                title={activeEmbed.title}
                referrerPolicy="strict-origin-when-cross-origin"
                allow={activeEmbed.allowAutoplay
                  ? 'autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen'
                  : 'accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen'}
                allowFullScreen
              />
            )}
          </div>
        )}

        {/* ── Grid view ── */}
        {!activeEmbed && (
          <div className="lp-body">

            {/* Video tiles — Live streaming has its own dedicated header
                button now, so the live banner is no longer rendered here. */}
            <div className="lp-section-label">{t(T.videos)}</div>
            <div className="lp-video-grid">
              {VIDEOS.map((video) => (
                <button
                  key={video.id}
                  className={`lp-video-tile${video.videoId ? ' lp-video-tile--embeddable' : ''}`}
                  onClick={() => handleVideoTileClick(video)}
                  aria-label={t(video.title)}
                >
                  {video.videoId ? (
                    <div className="lp-tile-thumb-wrap">
                      <img
                        className="lp-tile-thumb"
                        src={ytThumb(video.videoId)}
                        alt=""
                        loading="lazy"
                      />
                      <div className="lp-tile-play">▶</div>
                    </div>
                  ) : (
                    <div className="lp-tile-thumb-wrap lp-tile-thumb-wrap--placeholder">
                      <span className="lp-tile-yt-icon">▶</span>
                    </div>
                  )}
                  <div className="lp-tile-info">
                    <div className="lp-tile-title">{t(video.title)}</div>
                    <div className="lp-tile-subtitle">{t(video.subtitle)}</div>
                    {!video.videoId && (
                      <div className="lp-tile-external">{t(T.watchYT)}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Websites — desktop hides this section (mobile-only) via CSS */}
            <div className="lp-websites-section">
              <div className="lp-section-label">{t(T.websites)}</div>
              <div className="lp-websites-row">
                {WEBSITES.map((site) => (
                  <button
                    key={site.id}
                    className="lp-site-btn"
                    onClick={() => handleSiteClick(site)}
                  >
                    <span className="lp-site-icon">{site.icon}</span>
                    <span className="lp-site-label">{t(site.title)}</span>
                    <span className="lp-site-url">{t(site.subtitle)}</span>
                    <span className="lp-site-arrow">→</span>
                  </button>
                ))}
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default LinksPanel;
